import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trophy, ShieldCheck, Calendar as CalIcon } from "lucide-react";
import AppLayout from "../components/AppLayout";
import Schedule from "../components/Schedule";
import ResultDialog from "../components/ResultDialog";
import EventShareDialog from "../components/EventShareDialog";
import InvitePlayersDialog from "../components/InvitePlayersDialog";
import AddToCalendarButton from "../components/AddToCalendarButton";
import AttendanceWidget from "../components/AttendanceWidget";
import GolfScorecard from "../components/GolfScorecard";
import GolfLeaderboard from "../components/GolfLeaderboard";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";

export default function LeagueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [l, setL] = useState(null);
  const [open, setOpen] = useState(null);
  const [scoreSignal, setScoreSignal] = useState(0);

  const fetchL = () => api.get(`/leagues/${id}`).then((r) => setL(r.data));
  useEffect(() => { fetchL(); }, [id]); // eslint-disable-line

  if (!l) return <AppLayout><div className="pp-card p-10 text-center text-zinc-500">Loading...</div></AppLayout>;

  const joined = user && l.standings?.some((p) => p.user_id === user.id);
  const isOrganizer = user && (l.organizer_id === user.id || ["admin", "federation"].includes(user.role));
  const isGolf = l.sport?.slug === "golf" && !!l.golf;
  const playersById = Object.fromEntries((l.standings || []).map((p) => [p.user_id, p.user]));

  const join = async () => {
    if (!user) { navigate("/login"); return; }
    try { await api.post(`/leagues/${id}/join`); toast.success(t("joined")); fetchL(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const generateSchedule = async () => {
    try { await api.post(`/leagues/${id}/schedule/generate`); toast.success("Schedule generated"); fetchL(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const recordFix = async (round, idx, score_a, score_b) => {
    try { await api.post(`/leagues/${id}/schedule/result`, { round, idx, score_a, score_b }); toast.success("Saved"); fetchL(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <AppLayout>
      <Link to="/leagues" data-testid="back-leagues" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1 mb-6"><ArrowLeft size={14} /> {t("leagues")}</Link>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 pp-card-elevated p-8">
          <div className="text-xs text-[#00E676] uppercase tracking-wider flex items-center gap-1">
            <Trophy size={14} /> {l.sport?.name}
            {l.is_verified && <span className="ms-2 inline-flex items-center gap-1 text-[#00E676]"><ShieldCheck size={12} /> Verified</span>}
            {l.visibility === "private" && <span className="ms-2 pp-chip text-[10px] text-[#FFD60A] border-[#FFD60A]/40" data-testid="league-private-badge">Private</span>}
          </div>
          <h1 className="font-heading text-3xl font-black mt-2" data-testid="league-title">{l.title}</h1>
          <p className="text-zinc-400 mt-3">{l.description}</p>
          <div className="mt-4 text-sm text-zinc-400">{t("season")}: <span className="text-white">{l.season_name}</span></div>
          <div className="mt-2 text-sm text-zinc-400">{t("starts")}: <span className="text-white">{l.all_day ? new Date(l.start_date).toLocaleDateString() + " · All day" : new Date(l.start_date).toLocaleString()}</span></div>
          <div className="mt-2 text-sm text-zinc-400">{t("ends")}: <span className="text-white">{l.all_day ? new Date(l.end_date).toLocaleDateString() : new Date(l.end_date).toLocaleString()}</span></div>
          <div className="mt-2 text-sm text-zinc-400">{t("entry_fee")}: <span className="text-white">{l.entry_fee > 0 ? `${l.entry_fee} ${l.currency}` : t("free")}</span></div>
          <div className="mt-6 flex flex-wrap gap-2">
            {joined ? <span className="pp-chip pp-chip-active" data-testid="league-joined">{t("joined")}</span> :
              <button data-testid="league-join-btn" onClick={join} className="pp-btn-primary px-5 py-3 rounded-xl">{t("join")}</button>}
            <AddToCalendarButton
              kind="league"
              id={id}
              start={l.start_date}
              end={l.end_date}
              title={l.title}
              location={l.location}
              description={l.description}
              testIdSuffix="league"
            />
            {isOrganizer && !l.schedule && <button data-testid="league-generate-schedule" onClick={generateSchedule} className="pp-btn-secondary px-5 py-3 rounded-xl flex items-center gap-2"><CalIcon size={14} /> Generate schedule</button>}
            {isOrganizer && (
              <>
                <InvitePlayersDialog eventKind="league" eventId={id} onInvited={fetchL} />
                <EventShareDialog eventKind="league" eventId={id} eventTitle={l.title} />
              </>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 pp-card p-6">
          <h3 className="font-heading text-xl font-bold mb-4">{t("standings")}</h3>
          <div className="mb-4">
            <AttendanceWidget eventKind="league" eventId={id} />
          </div>
          <table className="w-full text-sm" data-testid="league-standings">
            <thead className="text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="text-start py-2">{t("rank")}</th><th className="text-start">{t("player_col")}</th><th className="text-end">{t("points")}</th></tr>
            </thead>
            <tbody>
              {(l.standings || []).map((p) => (
                <tr key={p.id} className="border-t border-white/5">
                  <td className="py-2 text-zinc-500">#{p.ranking}</td>
                  <td>{p.user?.first_name} {p.user?.last_name}</td>
                  <td className="text-end font-bold text-[#00E676]">{p.points}</td>
                </tr>
              ))}
              {(l.standings || []).length === 0 && <tr><td colSpan={3} className="py-6 text-center text-zinc-500">No players yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {l.schedule && (
        <section className="mt-10">
          <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2"><CalIcon size={20} className="text-[#00E676]" /> Schedule</h2>
          <Schedule schedule={l.schedule} playersById={playersById}
            onRecord={isOrganizer ? (ri, mi, fix) => setOpen({ ri, mi, fix }) : null} />
          <ResultDialog
            open={!!open}
            onOpenChange={(v) => { if (!v) setOpen(null); }}
            title={open ? `${playersById[open.fix.a]?.first_name || "A"} vs ${playersById[open.fix.b]?.first_name || "B"}` : ""}
            playerA={{ name: open ? `${playersById[open.fix.a]?.first_name || ""}` : "" }}
            playerB={{ name: open ? `${playersById[open.fix.b]?.first_name || ""}` : "" }}
            onSubmit={({ score_a, score_b }) => { if (open) recordFix(open.ri, open.mi, score_a, score_b); setOpen(null); }}
            testIdPrefix="league-result"
          />
        </section>
      )}

      {isGolf && (
        <section className="mt-10 space-y-4" data-testid="league-golf-section">
          {joined && <GolfScorecard event={l} eventKind="league" onSaved={() => setScoreSignal((s) => s + 1)} />}
          <GolfLeaderboard eventId={id} eventKind="league" refreshSignal={scoreSignal} />
        </section>
      )}
    </AppLayout>
  );
}
